import { z } from 'zod';

import { SKU_CODE } from './sku-rule-form-schema';

const SLUG = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

/** One attribute assignment as the editor holds it; its place in the list is its position. */
export const productTypeAssignmentSchema = z.object({
  attributeId: z.string().min(1),
  isRequired: z.boolean(),
});

export const MAX_SUGGESTED_FAQ = 30;

/** How a suggested question is matched: the backend refuses repeats and products hide ones they have. */
export function faqQuestionKey(question: string): string {
  return question.trim().toLowerCase();
}

export const suggestedFaqItemSchema = z.object({
  question: z
    .string()
    .trim()
    .min(1, { error: 'productTypes:validation.faqQuestionRequired' })
    .max(300, { error: 'productTypes:validation.faqQuestionLength' }),
  answer: z
    .string()
    .trim()
    .min(1, { error: 'productTypes:validation.faqAnswerRequired' })
    .max(4000, { error: 'productTypes:validation.faqAnswerLength' }),
});

/**
 * A product type as the editor edits it. Attribute labels are looked up from the dictionary for
 * display; the form only holds what is sent. `defaultValue` per assignment is not edited here.
 */
export const productTypeFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, { error: 'productTypes:validation.nameRequired' })
    .max(120, { error: 'productTypes:validation.nameLength' }),
  slug: z
    .string()
    .trim()
    .min(1, { error: 'productTypes:validation.slugRequired' })
    .max(120, { error: 'productTypes:validation.slugLength' })
    .regex(SLUG, { error: 'productTypes:validation.slugFormat' }),
  description: z.string().trim().max(2000, { error: 'productTypes:validation.descriptionLength' }),
  /** The {type} token of SKU templates (D-161); empty means the first two letters of the slug. */
  skuCode: z
    .string()
    .trim()
    .refine((v) => v === '' || SKU_CODE.test(v), {
      error: 'productTypes:validation.skuCodeFormat',
    }),
  position: z
    .string()
    .trim()
    .refine((v) => /^\d+$/.test(v), { error: 'productTypes:validation.position' }),
  attributes: z
    .array(productTypeAssignmentSchema)
    .max(100, { error: 'productTypes:validation.attributesMax' }),
  defaultCustomizationIds: z
    .array(z.string())
    .max(50, { error: 'productTypes:validation.customizationsMax' }),
  suggestedFaq: z
    .array(suggestedFaqItemSchema)
    .max(MAX_SUGGESTED_FAQ, { error: 'productTypes:validation.faqMax' })
    .superRefine((items, ctx) => {
      const seen = new Set<string>();
      items.forEach((item, index) => {
        const key = faqQuestionKey(item.question);
        if (key && seen.has(key)) {
          ctx.addIssue({
            code: 'custom',
            path: [index, 'question'],
            message: 'productTypes:validation.faqDuplicate',
          });
        }
        seen.add(key);
      });
    }),
});

export type ProductTypeFormInput = z.input<typeof productTypeFormSchema>;
export type ProductTypeFormValues = z.output<typeof productTypeFormSchema>;
